"use client";

import React, { useRef, useCallback, useEffect, type MouseEvent, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface LiquidGlassCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  className?: string;
  spotlightColor?: string;
  enableTilt?: boolean;
  maxTiltAngle?: number;
}

export function LiquidGlassCard({
  children,
  className,
  spotlightColor = "rgba(201, 168, 76, 0.18)",
  enableTilt = false,
  maxTiltAngle = 1.2,
  style,
  ...props
}: LiquidGlassCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const boundsRef = useRef<{ left: number; top: number; width: number; height: number } | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const isTouchRef = useRef(false);

  useEffect(() => {
    isTouchRef.current =
      typeof window !== "undefined" &&
      (window.matchMedia("(pointer: coarse)").matches || "ontouchstart" in window);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (isTouchRef.current || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    boundsRef.current = {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (isTouchRef.current || !cardRef.current) return;

      if (!boundsRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        boundsRef.current = {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        };
      }

      const clientX = e.clientX;
      const clientY = e.clientY;

      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }

      rafIdRef.current = requestAnimationFrame(() => {
        if (!cardRef.current || !boundsRef.current) return;

        const { left, top, width, height } = boundsRef.current;
        const x = clientX - left;
        const y = clientY - top;

        cardRef.current.style.setProperty("--mouse-x", `${x}px`);
        cardRef.current.style.setProperty("--mouse-y", `${y}px`);

        if (enableTilt) {
          const centerX = width / 2;
          const centerY = height / 2;
          const rx = ((y - centerY) / centerY) * -maxTiltAngle;
          const ry = ((x - centerX) / centerX) * maxTiltAngle;
          cardRef.current.style.transform = `perspective(1200px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg)`;
        }

        rafIdRef.current = null;
      });
    },
    [enableTilt, maxTiltAngle],
  );

  const handleMouseLeave = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    boundsRef.current = null;

    if (!cardRef.current) return;
    cardRef.current.style.setProperty("--mouse-x", "-999px");
    cardRef.current.style.setProperty("--mouse-y", "-999px");
    if (enableTilt) {
      cardRef.current.style.transform = "perspective(1200px) rotateX(0deg) rotateY(0deg)";
    }
  }, [enableTilt]);

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        ...style,
        ["--spotlight-color" as any]: spotlightColor,
        willChange: enableTilt ? "transform" : undefined,
      }}
      className={cn(
        "liquid-glass-card group relative isolate overflow-hidden rounded-3xl",
        className,
      )}
      {...props}
    >
      {/* Dynamic Specular Spotlight rendered via CSS variables - zero React state updates */}
      <div
        className="pointer-events-none absolute -inset-px rounded-3xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{
          background: `radial-gradient(420px circle at var(--mouse-x, -999px) var(--mouse-y, -999px), var(--spotlight-color, ${spotlightColor}), transparent 60%)`,
        }}
      />

      {/* Top Specular Rim */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-70" />

      {/* Card Content */}
      <div className="relative z-10 h-full">{children}</div>
    </div>
  );
}

export default LiquidGlassCard;
